/**
 * SB6-II-01 — inline Planned Date cell.
 *
 * Date-only (OQ-II-2). Renders the row's `plannedDate` (YYYY-MM-DD) or
 * `"-"`. Interactive only for PM / TA (role-gated). Clicking opens a
 * `<Popover>` with a date input; commit fires `PATCH {plannedDate}` with
 * optimistic update + rollback + toast.
 *
 * Implementation note: the repo has **no** date-adapter library
 * (date-fns / dayjs / luxon) installed, which MUI X `<DatePicker>` +
 * `<LocalizationProvider>` require. To avoid adding a runtime dependency
 * for a single date-only field, this cell uses a native
 * `<TextField type="date">` — date-only, fully accessible, zero new
 * deps. If a project-wide DatePicker adapter is later adopted, swap the
 * input here.
 *
 * Permission note: there is no dedicated `inspection_instruction.*`
 * planned-date permission key in the role matrix, so per the story we
 * reuse `"inspection_instruction.update_state"` (the existing II key,
 * conditional-true for PM/TA) to gate inline planned-date edits. The BE
 * remains the security boundary.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { useToast } from "../account/ToastContext.js";
import { iiKeys } from "./keys.js";
import { usePatchInspectionInstructionMutation } from "./mutations.js";
import { applyOptimisticRowPatch } from "./optimisticListUpdate.js";
import type { IiListItem, IiListResponse } from "./types.js";

export interface PlannedDateCellProps {
  row: IiListItem;
}

export function PlannedDateCell({ row }: PlannedDateCellProps): ReactElement {
  const qc = useQueryClient();
  const { toast } = useToast();
  // See file header — reuse the existing II update_state key (no
  // dedicated planned-date key exists). conditional-true for PM/TA.
  const canEdit = useCanEditResourceFor("inspection_instruction.update_state");
  const patch = usePatchInspectionInstructionMutation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState(row.plannedDate ?? "");
  const open = anchorEl !== null;

  useEffect(() => {
    if (open) setDraft(row.plannedDate ?? "");
  }, [open, row.plannedDate]);

  const label = row.plannedDate ?? "-";

  if (!canEdit) {
    return (
      <Typography variant="body2" component="span">
        {label}
      </Typography>
    );
  }

  function handleOpen(e: React.MouseEvent<HTMLElement>): void {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }
  function handleClose(): void {
    setAnchorEl(null);
  }

  function commit(): void {
    const next = draft.trim().length > 0 ? draft : null;
    const family = iiKeys.lists();
    const snapshots = qc.getQueriesData<IiListResponse>({ queryKey: family });
    for (const [key, data] of applyOptimisticRowPatch(
      snapshots,
      row.inspectionInstructionId,
      { plannedDate: next },
    )) {
      if (data) qc.setQueryData(key, data);
    }
    patch.mutate(
      { id: row.inspectionInstructionId, body: { plannedDate: next } },
      {
        onError: () => {
          for (const [key, data] of snapshots) {
            if (data) qc.setQueryData(key, data);
          }
          toast("Couldn't update planned date — try again.", "error");
        },
        onSuccess: () => {
          toast("Planned date updated.");
        },
      },
    );
    handleClose();
  }

  return (
    <>
      <Box
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label={`Edit planned date (${label})`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        sx={{
          width: "100%",
          cursor: "pointer",
          minHeight: 24,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <Typography
          variant="body2"
          component="span"
          color={row.plannedDate ? "text.primary" : "text.secondary"}
        >
          {label}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 260, p: 1.5 } } }}
      >
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            Planned date
          </Typography>
          <TextField
            type="date"
            size="small"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            slotProps={{
              htmlInput: { "aria-label": "Planned date" },
              inputLabel: { shrink: true },
            }}
            fullWidth
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handleClose}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={commit}>
              Save
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
